import type { Project, ProjectSpecialVo, PurchaseDemandVo } from "@zhihaoscm/entity";
import { PURCHASE_TYPE } from "@zhihaoscm/meta";
import { useEffect, useMemo, useState } from "react";
import {
    BaseItem as ZhBaseItem,
    Button as ZhButton,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    GridItem as ZhGridItem,
    Input as ZhInput,
    Select as ZhSelect
} from "zhihao-ui-react";
import { projectService } from "@/api/services/projectService";
import { dateFormat } from "@/utils";
import { intendedDemandManage } from "../index";

export interface CreateProjectProps {
    visible?: boolean;
    onUpdateVisible?: (visible: boolean) => void;
    onConfirmed?: () => void;
    projectCommissionerList?: ProjectSpecialVo[];
    purchaseDemandVo?: PurchaseDemandVo;
}

type CreateForm = { purchaseDemandId?: number; name: string; remark: string; handlerId?: number };
type ProjectOption = { label: string; value: number; item: Project };

/** 生成项目弹窗：保持直采新建、联合采购加入已有项目及旧 API 提交契约。 */
export default function CreateProject({
    visible = false,
    onUpdateVisible,
    onConfirmed,
    projectCommissionerList = [],
    purchaseDemandVo
}: CreateProjectProps) {
    const demand = purchaseDemandVo?.purchaseDemand;
    const isDirect = PURCHASE_TYPE.DIRECT.match(demand?.purType);
    const [createType, setCreateType] = useState<"new" | "old">(isDirect ? "new" : "old");
    const [form, setForm] = useState<CreateForm>(() => ({
        purchaseDemandId: demand?.id,
        name: `${demand?.productTypeName || ""}_${purchaseDemandVo?.productType?.id || ""}_${PURCHASE_TYPE.getIntroByCode(
            demand?.purType ?? ""
        )}_${dateFormat(new Date()).replaceAll("-", "")}`,
        remark: ""
    }));
    const [projectId, setProjectId] = useState<number>();
    const [projects, setProjects] = useState<ProjectOption[]>([]);
    const [selectedProject, setSelectedProject] = useState<Project>();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!visible || !demand || isDirect) return;
        void projectService
            .findBySupplierAndSupplyChain({
                productId: purchaseDemandVo?.product?.id,
                supplierId: demand.supplierId,
                supplyChainId: demand.supplyChainId,
                buyerId: demand.buyerId
            })
            .then(items => setProjects(items.map(item => ({ label: item.name, value: item.id, item }))));
    }, [demand, isDirect, purchaseDemandVo, visible]);

    const commissionerOptions = useMemo(
        () => projectCommissionerList.map(item => ({ value: item.user.id, label: `${item.user.name}(${item.count})` })),
        [projectCommissionerList]
    );
    const close = () => onUpdateVisible?.(false);
    const updateForm = (key: keyof CreateForm, value: string | number | undefined) =>
        setForm(current => ({ ...current, [key]: value }));

    const submit = async () => {
        setError("");
        if (!demand?.id) return;
        if (createType === "new" && (!form.name.trim() || !form.remark.trim())) {
            setError("请输入项目名称和项目介绍");
            return;
        }
        if (createType === "old" && projectId == null) {
            setError("请选择项目");
            return;
        }
        setSubmitting(true);
        try {
            if (createType === "new") {
                await intendedDemandManage.createProject({
                    purchaseDemandId: demand.id,
                    name: form.name.trim(),
                    remark: form.remark.trim(),
                    handlerId: form.handlerId
                });
            } else {
                await projectService.joinProject({ projectId: projectId!, purchaseDemandId: demand.id });
            }
            close();
            onConfirmed?.();
        } catch {
            setError("项目生成失败，请稍后重试");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <ZhDialog
            open={visible}
            title="生成项目"
            width={842}
            onCancel={close}
            footer={
                <div className="flex justify-end gap-2">
                    <ZhButton onClick={close}>取消</ZhButton>
                    <ZhButton type="primary" loading={submitting} onClick={() => void submit()}>
                        提交
                    </ZhButton>
                </div>
            }
        >
            {!isDirect ? (
                <div className="mb-4 flex gap-2 rounded bg-gray-100 p-1">
                    <ZhButton type={createType === "old" ? "primary" : "default"} onClick={() => setCreateType("old")}>
                        加入已有项目
                    </ZhButton>
                    <ZhButton type={createType === "new" ? "primary" : "default"} onClick={() => setCreateType("new")}>
                        生成新项目
                    </ZhButton>
                </div>
            ) : null}
            {createType === "new" ? (
                <ZhGrid cols={1} gap={[0, 16]}>
                    <ZhGridItem label="项目名称">
                        <ZhInput
                            maxLength={30}
                            value={form.name}
                            placeholder="请输入项目名称"
                            onChange={event => updateForm("name", event.target.value)}
                        />
                    </ZhGridItem>
                    <ZhGridItem label="项目介绍">
                        <ZhInput.TextArea
                            maxLength={200}
                            showCount
                            value={form.remark}
                            placeholder="请输入项目介绍"
                            onChange={event => updateForm("remark", event.target.value)}
                        />
                    </ZhGridItem>
                    <ZhGridItem label="选择项目专员">
                        <ZhSelect
                            allowClear
                            options={commissionerOptions}
                            value={form.handlerId}
                            onChange={value => updateForm("handlerId", value as number)}
                        />
                    </ZhGridItem>
                </ZhGrid>
            ) : (
                <>
                    <ZhSelect
                        className="w-full"
                        showSearch
                        allowClear
                        placeholder="请选择项目"
                        options={projects}
                        value={projectId}
                        onChange={value => {
                            const nextId = value == null ? undefined : Number(value);
                            setProjectId(nextId);
                            setSelectedProject(projects.find(item => item.value === nextId)?.item);
                        }}
                    />
                    {selectedProject ? (
                        <ZhGrid className="mt-4" cols={2}>
                            <ZhBaseItem
                                label="采购类型"
                                value={PURCHASE_TYPE.getIntroByCode(selectedProject.purType)}
                            />
                            <ZhBaseItem label="名称" value={selectedProject.name} />
                            <ZhGridItem span={2}>
                                <ZhBaseItem label="介绍" value={selectedProject.remark} />
                            </ZhGridItem>
                        </ZhGrid>
                    ) : null}
                </>
            )}
            {error ? <p className="mt-3 text-red-500">{error}</p> : null}
        </ZhDialog>
    );
}
